import { AlertCircle, FileMinus, FilePlus, Pencil } from "lucide-react"
import { useMemo } from "react"
import { FileDiffViewer, FileViewer, parsePatchFiles } from "../../FilesAndDiffs"
import type { FileChangeGroup, GroupRenderer } from "../../events/messageGroups"
import { getFileName } from "../../utils/paths"

function FileChangeContent({ group }: { group: FileChangeGroup }) {
    const parsedDiff = useMemo(() => {
        if (!group.diff || group.isPending) return null
        try {
            const cacheKey = `file-change:${group.toolUseId}:${group.changeIndex}:${group.filePath}`
            const parsed = parsePatchFiles(group.diff, cacheKey)
            return parsed[0]?.files[0] ?? null
        } catch {
            return null
        }
    }, [group.changeIndex, group.diff, group.filePath, group.isPending, group.toolUseId])

    if (parsedDiff) {
        return <FileDiffViewer fileDiff={parsedDiff} diffStyle="unified" commentHandlers={null} />
    }

    const details = [`${group.status}: ${group.kind} ${group.filePath}`]
    if (group.diff) details.push("", group.diff)

    return (
        <FileViewer
            file={{ name: "file-change.txt", contents: details.join("\n"), lang: "text" }}
            disableFileHeader
            disableLineNumbers
            commentHandlers={null}
        />
    )
}

function getIcon(kind: string) {
    switch (kind) {
        case "add":
            return <FilePlus size="0.85em" className="text-success flex-shrink-0" />
        case "delete":
            return <FileMinus size="0.85em" className="text-error flex-shrink-0" />
        default:
            return <Pencil size="0.85em" className="text-primary flex-shrink-0" />
    }
}

function getKindLabel(kind: string): string {
    switch (kind) {
        case "add":
            return "Add"
        case "delete":
            return "Delete"
        case "update":
            return "Update"
        default:
            return kind ? kind[0].toUpperCase() + kind.slice(1) : "Change"
    }
}

function computeDiffStats(diff: string | undefined): { additions: number; deletions: number } | null {
    if (!diff) return null

    let additions = 0
    let deletions = 0
    for (const line of diff.split("\n")) {
        if (line.startsWith("+++") || line.startsWith("---")) continue
        if (line.startsWith("+")) additions += 1
        if (line.startsWith("-")) deletions += 1
    }

    return additions > 0 || deletions > 0 ? { additions, deletions } : null
}

export const fileChangeRenderer: GroupRenderer<FileChangeGroup> = {
    getLabel: (group) => `${getKindLabel(group.kind)} ${getFileName(group.filePath)}`,
    getIcon: (group) => getIcon(group.kind),
    getStatusIcon: (group) => {
        if (group.isError) return <AlertCircle size="1em" className="text-error flex-shrink-0" />
        return null
    },
    getHeaderInfo: (group) => {
        const stats = computeDiffStats(group.diff)
        if (!stats) return <span className="text-muted text-xs">{group.kind}</span>
        return (
            <span className="text-muted text-xs">
                {stats.additions > 0 && <span className="text-success">+{stats.additions}</span>}
                {stats.additions > 0 && stats.deletions > 0 && " "}
                {stats.deletions > 0 && <span className="text-error">-{stats.deletions}</span>}
            </span>
        )
    },
    renderContent: (group) => <FileChangeContent group={group} />,
}
